<script lang="ts">
export default {
  name: 'PagingControl',
  components: {}
};
</script>

<script setup lang="ts">
import { ChevronLeft, ChevronRight } from '@lucide/vue';
import { nextTick, ref, onBeforeUnmount, onMounted } from 'vue';

const emit = defineEmits<{
  'set-page': [number],
  'previous-page': [],
  'next-page': []
}>();

// PROPS
// ------------------------------------------------
const props = defineProps({
  currentPage: {
    type: Number,
    required: true
  },
  totalPages: {
    type: Number,
    required: true
  }
});

// REFS
// ------------------------------------------------
const pageSelectionOpen = ref(false);
const pageSelector = ref<HTMLInputElement>();

// METHODS
// ------------------------------------------------
function openPageSelector() {
  pageSelectionOpen.value = true;
  nextTick(() => {
    pageSelector.value?.select();
  });
}

function setPage() {
  const page = parseInt(pageSelector.value?.value ?? '');

  if (!isNaN(page) && page >= 1 && page <= props.totalPages) {
    emit('set-page', page);
  }
  
  pageSelectionOpen.value = false;
}

function handleKeyDown(e: KeyboardEvent) {
  if ((e.key === 'Enter' || e.key === 'Escape') && pageSelectionOpen.value) {
    setPage();
  }
}

// LIFECYCLE
// ------------------------------------------------
onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
});
</script>

<template>
  <div class="flex justify-center">
    <div class="join mt-6">
      <button @click="emit('previous-page')" class="join-item btn" :class="{'btn-disabled': currentPage && currentPage === 1}">
        <ChevronLeft class="h-6 w-4" />
      </button>

      <button v-if="!pageSelectionOpen && currentPage !== 0 && totalPages !== 0" @click="openPageSelector"
              class="join-item btn font-medium transition-none">
        Page {{ currentPage }} / {{ totalPages }}
      </button>

      <div v-if="pageSelectionOpen" class="join-item btn font-medium transition-none">
        Page <input ref="pageSelector" type="text" :value="currentPage"
                    class="w-8 text-center rounded-[1px]"
                    @blur="setPage" /> / {{ totalPages }}
      </div>

      <button @click="emit('next-page')" class="join-item btn"
              :class="{'btn-disabled': !totalPages || !currentPage || (currentPage === totalPages)}">
        <ChevronRight class="h-6 w-4" />
      </button>
    </div>
  </div>
</template>
